import {isSameDay} from 'date-fns';
import {useDateTimeFormatter} from 'platform/locale';
import {match} from 'ts-pattern';

import i18n from '@dms/i18n';

import {OutlookCalendarEvent} from '../../../utils/OutlookCalendarEvent';

const RANGE_SEPARATOR = ' – ';
const DETAIL_SEPARATOR = ' · ';

/**
 * Human readable span of an Outlook event.
 *
 * All-day events already carry an inclusive end (the exclusive Graph end is pulled back one day
 * when the event is transformed), so both boundaries can be printed as plain dates. Timed events
 * print times, and drop the repeated date when they start and end on the same day.
 */
export function getOutlookEventDateSpan(
  event: OutlookCalendarEvent,
  formatDateTime: ReturnType<typeof useDateTimeFormatter>
) {
  const isSingleDay = isSameDay(event.start, event.end);

  return match([event.isAllDay ?? false, isSingleDay])
    .with([true, true], () => {
      const day = formatDateTime('dateMedium', event.start);
      return `${day}${DETAIL_SEPARATOR}${i18n.t('entity.calendarEvent.labels.allDay')}`;
    })
    .with([true, false], () => {
      const span = `${formatDateTime('dateMedium', event.start)}${RANGE_SEPARATOR}${formatDateTime('dateMedium', event.end)}`;
      return `${span}${DETAIL_SEPARATOR}${i18n.t('entity.calendarEvent.labels.allDay')}`;
    })
    .with([false, true], () => {
      const day = formatDateTime('dateMedium', event.start);
      const from = formatDateTime('timeShort', event.start);
      const to = formatDateTime('timeShort', event.end);
      return `${day}${DETAIL_SEPARATOR}${from}${RANGE_SEPARATOR}${to}`;
    })
    .with([false, false], () => {
      const from = formatDateTime('dateTimeMedium', event.start);
      const to = formatDateTime('dateTimeMedium', event.end);
      return `${from}${RANGE_SEPARATOR}${to}`;
    })
    .exhaustive();
}
